import { FontFamily, useZakeke } from '@zakeke/zakeke-configurator-react';
import { debounce } from 'lodash';
import { FC, useEffect, useState } from 'react';
import { CSSObjectWithLabel, GroupBase, OptionProps, SingleValueProps, components } from 'react-select';
import styled from 'styled-components';
import { T, wrapperJoin } from '../../Helpers';
import { Button, Columns, Icon, TextArea } from '../Atomic';

import type { PropChangeHandler } from '../layout/Designer';

import { ReactComponent as CurveIcon } from '../../assets/icons/bezier-curve-solid.svg';
import { ReactComponent as BoldIcon } from '../../assets/icons/bold-solid.svg';
import { ReactComponent as ItalicSolid } from '../../assets/icons/italic-solid.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/times-solid.svg';
import AdvancedSelect from './AdvancedSelect';
import { FormControl } from './FormControl';
import ColorPicker from './colorpicker';

export interface EditTextItem {
	guid: string;
	name: string;
	text: string;
	fillColor: string;
	fontFamily: string;
	fontWeight: string;
	fontSize: number;
	isTextOnPath: boolean;
	constraints: { [key: string]: any } | null;
}

const defaultColorsPalette = ['#000000', '#FFFFFF'];

enum ItemType {
	Text = 0,
	Image = 1
}

export interface TextItem {
	type: ItemType;
	areaId: number;
	guid: string;
	name: string;
	text: string;
	strokeColor: string;
	strokeWidth: number;
	fillColor: string;
	fontFamily: string;
	fontSize: number;
	fontWeight: string;
	fontStyle: string | undefined;
	fontStretch: string;
	justification: string;
	isTextOnPath: boolean;
	constraints: {
		[key: string]: any;
	} | null;
}

const RightToolsContainer = styled.div`
	display: flex;
	flex-direction: row;
	grid-gap: 10px;
`;

const ItemTextContainer = styled.div``;

const TextToolsContainer = styled.div`
	display: flex;
	flex-direction: row;
	grid-gap: 10px;
	flex-wrap: wrap;
`;

const TextButtonsContainer = styled.div`
	width: 50%;
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-gap: 5px;
`;

const ColorPickerContainer = styled.div`
	margin-right: 5px;
	width: calc(50% - 30px);
`;

const ColorsContainer = styled.div`
	display: flex;
	flex-direction: row;
	padding-bottom: 20px;
	border-bottom: 1px #ccc dotted;
`;

const SinglePaletteItem = styled.div<{ color: string; selected: boolean }>`
	width: 20px;
	height: 20px;
	background-color: ${(props) => props.color};
	border: 1px lightgray solid;
	cursor: pointer;

	${(props) => props.selected && `border: 1px black solid;`}

	&:hover {
		opacity: 0.6;
	}
`;

const TextColorsContainer = styled.div<{ $isDefaultPalette?: boolean }>`
	display: grid;
	${(props) =>
		!props.$isDefaultPalette &&
		`
    grid-template-columns: repeat(auto-fill,minmax(20px,1fr));
    grid-gap: 5px;`};
	/* grid-template-columns: repeat(auto-fill,minmax(20px,1fr)); */
	width: 100%;
`;

const FontCustomOption = styled.img`
	max-width: 100%;
	height: 24px;
	object-fit: contain;
`;

const FontCustomSingleValue = styled.img`
	max-width: 100%;
	height: 24px;
	object-fit: contain;
`;

const FontCustomSingleValueContainer = styled.div`
	display: flex;
	place-content: center;
	width: 100%;
	height: 100%;
`;

const FontOption = (props: JSX.IntrinsicAttributes & OptionProps<any, boolean, GroupBase<any>>) => {
	return (
		<components.Option {...props}>
			{<FontCustomOption src={props.data.imageUrl} alt={props.data.name} />}
		</components.Option>
	);
};

const FontSingleValue = (props: JSX.IntrinsicAttributes & SingleValueProps<any, boolean, GroupBase<any>>) => {
	return (
		<components.SingleValue {...props}>
			<FontCustomSingleValueContainer>
				{<FontCustomSingleValue src={props.data.imageUrl} alt={props.data.name} />}
			</FontCustomSingleValueContainer>
		</components.SingleValue>
	);
};

const ItemText: FC<{
	item: EditTextItem;
	handleItemPropChange: PropChangeHandler;
	fonts?: FontFamily[];
	inDialog?: boolean;
	hideRemoveButton?: boolean;
}> = ({ item, handleItemPropChange, inDialog, hideRemoveButton }) => {
	const { 
		removeItem, 
		fonts, 
		getPrintingMethodsRestrictions, 
		getSanitationText, 
		moveItemDown, 
		moveItemUp, 
		defaultFontID,
		getFontWeights,
	} = useZakeke();
	console.log("sono item text component, item:", item);
	const constraints = item.constraints;
	const canEdit = constraints?.canEdit ?? true;
	const hasCurvedText = item.isTextOnPath;
	const isUpperCase = constraints?.toUppercase ?? false;

	const textRestrictions = getPrintingMethodsRestrictions();
	// Used for performance cache
	const [fillColor, setFillColor] = useState(item.fillColor);
	const [fontLoading, setFontLoading] = useState(false);
	const [dirtyCharInserted, setDirtyCharInserted] = useState([] as string[]);

	// Parse font weight according to new format: "400", "700" for regular text, "italic 400", "italic 700" for italic text
	const weightData = typeof item.fontWeight === 'number' ? [String(item.fontWeight)] : String(item.fontWeight).split(' ');
	const isItalic = weightData.length > 1 && weightData[0] === 'italic';
	
	// Enhanced parsing to handle legacy formats
	let numericWeight: number;
	if (isItalic) {
		numericWeight = parseInt(weightData[1]) || 400;
	} else {
		// Check if any part contains "bold" for legacy formats like "normal bold"
		const hasBold = weightData.some(part => part === 'bold');
		const firstPart = weightData[0];
		
		if (hasBold) {
			numericWeight = 700;
		} else if (firstPart === 'bold') {
			numericWeight = 700;
		} else if (firstPart === 'normal') {
			numericWeight = 400;
		} else {
			numericWeight = parseInt(firstPart) || 400;
		}
	}
	
	const isBold = numericWeight >= 700;
	
	// Debug logging
	console.log(`[FONT-WEIGHT-DEBUG] item.guid: ${item.guid}, fontWeight: "${item.fontWeight}", weightData:`, weightData, `isItalic: ${isItalic}, numericWeight: ${numericWeight}, isBold: ${isBold}`);

	// Usa getFontWeights dalla libreria invece di duplicare la logica
	const currentFont = fonts?.find((x) => x.name === item.fontFamily);
	console.log("111 currentFont:", currentFont);
	const availableFontWeights = getFontWeights(currentFont!) ;
	console.log("111 availableFontWeights:", availableFontWeights);

	const isBoldAvailable = () => {
		if (!currentFont) return true;
		console.log('DEBUG isBoldAvailable:', availableFontWeights.bold, availableFontWeights.italicBold);
		return availableFontWeights.bold || availableFontWeights.italicBold;
	};
	
	const isItalicAvailable = () => {
		if (!currentFont) return true;
		console.log('DEBUG isItalicAvailable:', availableFontWeights.italic, availableFontWeights.italicBold);
		return availableFontWeights.italic || availableFontWeights.italicBold;
	};

	const setItemTextDebounced = (value: string) => {
		handleItemPropChange?.(item, 'text', isUpperCase ? value.toUpperCase() : value);
		debounce(() => {
			const initialText = value;
			const sanitizationInfo = currentFont
				? getSanitationText(currentFont, value)
				: {
						sanitizedText: value,
						dirtyChars: []
				  };
			setDirtyCharInserted(sanitizationInfo.dirtyChars);
			const text = sanitizationInfo.sanitizedText;

			if (text !== initialText) {
				handleItemPropChange?.(item, 'text', isUpperCase ? text.toUpperCase() : text);
			}
		}, 500)();
	};

	// Simplified font change - environment handles font weight automatically
	const handleFontChange = (font: string) => {
		handleItemPropChange(item, 'font-family', font);
		setItemTextDebounced(item.text);
	};

	useEffect(() => {
		handleFontChange(item.fontFamily);
		//eslint-disable-next-line
	}, []);

	if (item)
		return (
			<ItemTextContainer>
				<FormControl
					label={item.name || T._('Text', 'Composer')}
					rightComponent={
						!hideRemoveButton &&
						item.constraints!.canDelete && (
							<RightToolsContainer>
								<Icon onClick={() => moveItemUp(item.guid)}>
									<Icon>▲</Icon>
								</Icon>
								<Icon onClick={() => moveItemDown(item.guid)}>
									<Icon>▼</Icon>
								</Icon>
								<Icon onClick={() => removeItem(item.guid)}>
									<CloseIcon />
								</Icon>
							</RightToolsContainer>
						)
					}
				>
					<TextArea
						defaultValue={isUpperCase ? item.text.toUpperCase() : item.text}
						onChange={(e) => {
							e.currentTarget.value = e.currentTarget.value.replace('⠀', '');
							setItemTextDebounced(e.currentTarget.value);
						}}
						maxLength={!item.constraints ? null : item.constraints.maxNrChars || null}
						disabled={!canEdit || fontLoading}
					/>
					{dirtyCharInserted.length > 0 && currentFont && (
						<div style={{ color: 'red' }}>
							{T._(
								`The following characters have been removed as they are not supported by the font ${
									currentFont.name
								}: ${wrapperJoin(dirtyCharInserted, ',', '"', '"')}`,
								'Composer'
							)}{' '}
						</div>
					)}
				</FormControl>

				<TextToolsContainer>
					{(!constraints || constraints.canChangeFontFamily) && (
						<FormControl label={T._('Font', 'Composer')}>
							<AdvancedSelect
								components={{
									Option: FontOption,
									SingleValue: FontSingleValue
								}}
								styles={{
									container: (base) =>
										({
											...base,
											width: 200
										} as CSSObjectWithLabel)
								}}
								isSearchable={false}
								options={fonts}
								isDisabled={fontLoading}
								menuPosition='fixed'
								value={(() => {
									// Se l'item ha già un font, usalo
									let selectedFont = fonts!.find((x) => x.name === item.fontFamily);
									
									// Se non è stato trovato un font e defaultFontID esiste, cerca il font di default
									if (!selectedFont && defaultFontID !== null && defaultFontID !== undefined) {
										selectedFont = fonts!.find((font) => (font as any).fontID === defaultFontID);
									}
									
									return [selectedFont];
								})()}
								onChange={(font: any) => {
									item.fontFamily = font.name;
									setFontLoading(true);
									handleFontChange(font.name);
									setTimeout(() => {
										setFontLoading(false);
									}, 2000);
								}}
							/>
						</FormControl>
					)}
					{(textRestrictions.allowedBold ||
						textRestrictions.allowedItalic ||
						textRestrictions.allowedCurved) && (
						<TextButtonsContainer>
							{(!constraints || constraints.canChangeFontWeight) &&
								(textRestrictions.allowedBold || textRestrictions.allowedItalic) && (
									<FormControl label={T._('Style', 'Composer')}>
										<Columns
											columns={
												textRestrictions.allowedBold && textRestrictions.allowedItalic ? 2 : 1
											}
										>
											{textRestrictions.allowedBold && (
											<Button
												outline
												selected={isBold && isBoldAvailable()}
												disabled={!isBoldAvailable()}
												onClick={() => {
													if (isBoldAvailable()) {
														handleItemPropChange(item, 'font-bold', !isBold, currentFont);
													}
												}}
													style={{
														opacity: isBoldAvailable() ? 1 : 0.3,
														cursor: isBoldAvailable() ? 'pointer' : 'not-allowed'
													}}
													title={!isBoldAvailable() ? 'Bold style not available for this font' : ''}
												>
													<Icon>
														<BoldIcon />
													</Icon>
												</Button>
											)}
											{textRestrictions.allowedItalic && (
												<Button
													outline
													selected={isItalic && isItalicAvailable()}
													disabled={!isItalicAvailable()}
													onClick={() => {
														if (isItalicAvailable()) {
															handleItemPropChange(item, 'font-italic', !isItalic, currentFont);
														}
													}}
													style={{
														opacity: isItalicAvailable() ? 1 : 0.3,
														cursor: isItalicAvailable() ? 'pointer' : 'not-allowed'
													}}
													title={!isItalicAvailable() ? 'Italic style not available for this font' : ''}
												>
													<Icon>
														<ItalicSolid />
													</Icon>
												</Button>
											)}
										</Columns>
									</FormControl>
								)}
							{(!constraints || constraints.canChangeTextPathMode) && textRestrictions.allowedCurved && (
								<FormControl label={T._('Curved', 'Composer')}>
									<Button
										outline
										selected={hasCurvedText}
										onClick={() => handleItemPropChange(item, 'text-path', !hasCurvedText)}
									>
										<Icon>
											<CurveIcon />
										</Icon>
									</Button>
								</FormControl>
							)}
						</TextButtonsContainer>
					)}
				</TextToolsContainer>

				{(!textRestrictions.disableTextColors ||
					!(textRestrictions.disableTextColors && textRestrictions.textColors.length === 1)) &&
					!!item.constraints?.canChangeFontColor && (
						<FormControl label={T._('Color', 'Composer')}>
							<ColorsContainer>
								{!textRestrictions.disableTextColors && (
									<ColorPickerContainer>
										<ColorPicker
											color={fillColor}
											onChange={(color) => {
												// handleFillColorChange(color);
												handleItemPropChange(item, 'font-color', color);
												setFillColor(color);
											}}
										/>
									</ColorPickerContainer>
								)}

								{!textRestrictions.disableTextColors && (
									<TextColorsContainer $isDefaultPalette>
										{defaultColorsPalette.map((hex) => (
											<SinglePaletteItem
												key={hex}
												onClick={() => {
													handleItemPropChange(item, 'font-color', hex);
													setFillColor(hex);
												}}
												selected={hex === fillColor}
												color={hex}
											/>
										))}
									</TextColorsContainer>
								)}

								{textRestrictions.disableTextColors && (
									<TextColorsContainer>
										{textRestrictions.textColors.map((textColor) => (
											<SinglePaletteItem
												key={textColor.colorCode}
												onClick={() => {
													handleItemPropChange(item, 'font-color', textColor.colorCode);
													setFillColor(textColor.colorCode);
												}}
												selected={textColor.colorCode === fillColor}
												color={textColor.colorCode}
											/>
										))}
									</TextColorsContainer>
								)}
							</ColorsContainer>
						</FormControl>
					)}
			</ItemTextContainer>
		);
	else return null;
};

export default ItemText;